import { Component } from '@theme/component';
import { isClickedOutside, normalizeString, onAnimationEnd } from '@theme/utilities';

/** Tolerance (px) when comparing list scrollHeight to parent slot for show-more. */
const UNIFIED_SHOW_MORE_OVERFLOW_SLACK_PX = 4;

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLUListElement[]} countryListItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLSelectElement | HTMLInputElement} languageInput - Select (standard form) or hidden input (unified drawer).
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 * @property {HTMLDivElement} [languageList] - Scroll container for unified drawer language list.
 * @property {(HTMLButtonElement | HTMLAnchorElement)[]} [languageListItems] - Unified drawer language controls.
 *
 * @extends {Component<FormRefs>}
 */
class LocalizationFormComponent extends Component {
  /** @type {number | null} */
  #unifiedCollapsedHeightPx = null;
  /** @type {number | null} */
  #unifiedLanguageCollapsedHeightPx = null;
  /** @type {ResizeObserver | null} */
  #unifiedLocalizationResizeObserver = null;
  /** @type {HTMLElement | null} */
  #countryListInnerScrollRoot = null;
  get #deferSubmit() {
    return this.dataset.deferSubmit === 'true';
  }

  get #unifiedForm() {
    return this.querySelector('.unified-localization-form');
  }

  connectedCallback() {
    super.connectedCallback();

    this.refs.search && this.refs.search.addEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('keydown', this.#onContainerKeyDown);
    this.#attachCountryListScrollListeners();

    this.resizeLanguageInput();
    this.#setupUnifiedLocalizationShowMore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.search && this.refs.search.removeEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('keydown', this.#onContainerKeyDown);
    this.#detachCountryListScrollListeners();
    this.#teardownUnifiedLocalizationShowMore();
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryInput, countryListItems, form } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('DOWN');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = countryListItems.find((item) => item.getAttribute('aria-selected') === 'true');

        if (focusedItem) {
          countryInput.value = focusedItem.dataset.value ?? '';

          if (this.dataset.unifiedSwitcher === 'true') {
            const { languageInput } = this.refs;
            const pairedLanguage = focusedItem.getAttribute('data-language-code') ?? '';
            const currentLanguage = languageInput?.value ?? '';
            const countryCode = focusedItem.dataset.value ?? '';

            if (languageInput) {
              if (countryCode === 'CZ') {
                languageInput.value = pairedLanguage || 'cs';
              } else if (currentLanguage && currentLanguage !== 'cs') {
                languageInput.value = currentLanguage;
              } else if (pairedLanguage) {
                languageInput.value = pairedLanguage;
              }
            }
          }

          if (this.#deferSubmit) {
            this.#syncCountryCurrentIndicator(countryInput.value);
          } else {
            form.submit();
          }
        }
        break;
      }
    }

    if (!this.refs.search) return;

    setTimeout(() => {
      const focusableItems = this.refs.countryListItems.filter((item) => !item.hasAttribute('hidden'));
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute('aria-activedescendant', focusedItem.id);
      } else {
        this.refs.search.setAttribute('aria-activedescendant', '');
      }
    });
  };

  /**
   * Selects a country.
   * When `data-unified-switcher` is set, also updates `language_code` from the row's
   * `data-language-code`. Non-Czech languages stay on EUR markets (non-CZ countries).
   *
   * @param {string} countryName - The name of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (countryName, event) => {
    event.preventDefault();
    const { countryInput, languageInput, form } = this.refs;

    countryInput.value = countryName;

    if (this.dataset.unifiedSwitcher === 'true' && languageInput) {
      const row = event.target instanceof Element ? event.target.closest('[data-language-code]') : null;
      const pairedLanguage = row?.getAttribute('data-language-code') ?? '';
      const currentLanguage = languageInput.value;

      if (countryName === 'CZ') {
        languageInput.value = pairedLanguage || 'cs';
      } else if (currentLanguage && currentLanguage !== 'cs') {
        languageInput.value = currentLanguage;
      } else if (pairedLanguage) {
        languageInput.value = pairedLanguage;
      }
    }

    if (this.#deferSubmit) {
      this.#syncCountryCurrentIndicator(countryName);
      return;
    }
    form?.submit();
  };

  /**
   * Selects a language (unified drawer: updates hidden `language_code`; defers submit when defer-submit is on).
   *
   * @param {string} isoCode - Language ISO code.
   * @param {Event} event - The click event.
   */
  selectLanguage = (isoCode, event) => {
    event.preventDefault();
    const { languageInput, form } = this.refs;
    if (!languageInput) return;

    languageInput.value = isoCode;
    if (this.#deferSubmit) {
      this.#syncLanguageCurrentIndicator(isoCode);
      return;
    }
    form?.submit();
  };

  /**
   * Expands/collapses the unified form country list (show more / show less).
   *
   * @param {MouseEvent} event - The click event.
   */
  toggleUnifiedCountriesExpand = (event) => {
    this.#toggleUnifiedShowMoreExpand(event, {
      buttonSelector: 'button.show-more-countries',
      getListEl: () => this.refs.countryList,
      getScrollRoot: () => this.#getCountryListScrollRoot(),
      expandedClass: 'country-selector__list--unified-expanded',
      moreLabelSelector: '.show-more-countries__label--more',
      lessLabelSelector: '.show-more-countries__label--less',
      getCollapsedHeightPx: () => this.#unifiedCollapsedHeightPx,
    });
  };

  /**
   * Expands/collapses the unified form language list (show more / show less).
   *
   * @param {MouseEvent} event - The click event.
   */
  toggleUnifiedLanguagesExpand = (event) => {
    this.#toggleUnifiedShowMoreExpand(event, {
      buttonSelector: 'button.show-more-languages',
      getListEl: () => this.refs.languageList,
      getScrollRoot: () => this.#getLanguageListScrollRoot(),
      expandedClass: 'language-selector__list--unified-expanded',
      moreLabelSelector: '.show-more-languages__label--more',
      lessLabelSelector: '.show-more-languages__label--less',
      getCollapsedHeightPx: () => this.#unifiedLanguageCollapsedHeightPx,
    });
  };

  /**
   * @param {MouseEvent} event
   * @param {object} config
   * @param {string} config.buttonSelector
   * @param {() => HTMLElement | undefined} config.getListEl
   * @param {() => HTMLElement | null | undefined} [config.getScrollRoot]
   * @param {string} config.expandedClass
   * @param {string} config.moreLabelSelector
   * @param {string} config.lessLabelSelector
   * @param {() => number | null} config.getCollapsedHeightPx
   */
  #toggleUnifiedShowMoreExpand(event, config) {
    const node = event.target instanceof Element ? event.target.closest(config.buttonSelector) : null;
    if (!(node instanceof HTMLButtonElement) || !this.#unifiedForm) return;

    const listEl = config.getListEl();
    if (!listEl) return;

    const scrollRoot = config.getScrollRoot?.() ?? null;
    const scrollTarget = scrollRoot ?? listEl;

    const expanded = listEl.classList.toggle(config.expandedClass);
    node.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    const moreLabel = node.querySelector(config.moreLabelSelector);
    const lessLabel = node.querySelector(config.lessLabelSelector);
    if (moreLabel instanceof HTMLElement && lessLabel instanceof HTMLElement) {
      moreLabel.hidden = expanded;
      lessLabel.hidden = !expanded;
    }

    if (expanded) {
      listEl.style.maxHeight = '';
    } else {
      const h = config.getCollapsedHeightPx();
      if (h != null && h > 0) {
        listEl.style.maxHeight = `${h}px`;
      }
      scrollTarget.scrollTop = 0;
    }
  }

  /**
   * Scroll container for the main country list (`scroll-hint` wrapping `ul.countries`), or legacy `ul.countries`.
   *
   * @returns {HTMLElement | null}
   */
  #getCountryListScrollRoot() {
    const hint = this.refs.countryList?.querySelector('scroll-hint.localization-form__countries-scroll');
    if (hint instanceof HTMLElement) return hint;
    const legacy = this.refs.countryList?.querySelector('ul.countries');
    return legacy instanceof HTMLElement ? legacy : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getLanguageListScrollRoot() {
    const hint = this.refs.languageList?.querySelector('scroll-hint.localization-form__languages-scroll');
    if (hint instanceof HTMLElement) return hint;
    const legacy = this.refs.languageList?.querySelector('ul.unified-localization-form__language-list');
    return legacy instanceof HTMLElement ? legacy : null;
  }

  /**
   * Scroll events do not bubble from nested scroll containers; listen on the list container and on the inner scroll root when used.
   */
  #attachCountryListScrollListeners() {
    this.#detachCountryListScrollListeners();
    const { countryList } = this.refs;
    if (!countryList) return;
    countryList.addEventListener('scroll', this.#onCountryListScroll);
    const inner = this.#getCountryListScrollRoot();
    if (inner && inner !== countryList) {
      inner.addEventListener('scroll', this.#onCountryListScroll);
      this.#countryListInnerScrollRoot = inner;
    }
  }

  #detachCountryListScrollListeners() {
    this.refs.countryList?.removeEventListener('scroll', this.#onCountryListScroll);
    this.#countryListInnerScrollRoot?.removeEventListener('scroll', this.#onCountryListScroll);
    this.#countryListInnerScrollRoot = null;
  }

  /**
   * @param {HTMLElement} listRoot
   * @param {HTMLElement} li
   */
  #unifiedListLiVisible(listRoot, li) {
    if (li.hasAttribute('hidden')) return false;
    let el = li.parentElement;
    while (el && el !== listRoot) {
      if (el.hasAttribute('hidden')) return false;
      el = el.parentElement;
    }
    return true;
  }

  /**
   * Vertical stride between list rows (includes margins between items).
   *
   * @param {HTMLElement[]} items - Visible row elements in tree order.
   */
  #unifiedListRowStride(items) {
    if (items.length >= 2) {
      const row0 = items[0];
      const row1 = items[1];
      if (!row0 || !row1) return 1;
      const a = row0.getBoundingClientRect().top;
      const b = row1.getBoundingClientRect().top;
      const stride = Math.ceil(b - a);
      return Math.max(1, stride);
    }
    const first = items[0];
    if (!first) return 1;
    const cs = getComputedStyle(first);
    const marginEnd = parseFloat(cs.marginBlockEnd) || parseFloat(cs.marginBottom) || 0;
    return Math.max(1, Math.ceil(first.offsetHeight + marginEnd));
  }

  /**
   * Collapsed list height (px) that clips after the last visible row — avoids subpixel bleed of the next row.
   *
   * @param {HTMLElement} listEl
   * @param {HTMLElement[]} items
   * @param {number} rows
   * @param {number} rowStride
   * @param {number} listPadY
   */
  #unifiedListClampedCollapsedHeightPx(listEl, items, rows, rowStride, listPadY) {
    const strideCap = Math.floor(rows * rowStride + listPadY);
    const idx = Math.min(rows, items.length) - 1;
    if (idx < 0 || !items[idx]) return Math.max(1, strideCap);

    const listTop = listEl.getBoundingClientRect().top;
    const measured = Math.floor(items[idx].getBoundingClientRect().bottom - listTop);
    if (measured < 1) return Math.max(1, strideCap);
    return Math.max(1, Math.min(strideCap, measured));
  }

  /**
   * True when list content is taller than the parent slot (list parent clientHeight, else list clientHeight).
   *
   * @param {HTMLElement} listEl
   * @returns {boolean}
   */
  #unifiedListContentOverflowsParentSlot(listEl) {
    const parent = listEl.parentElement;
    const parentH = parent instanceof HTMLElement ? parent.clientHeight : 0;
    const slotH = parentH > 0 ? parentH : listEl.clientHeight;
    return Math.ceil(listEl.scrollHeight) > Math.floor(slotH) + UNIFIED_SHOW_MORE_OVERFLOW_SLACK_PX;
  }

  /**
   * True when a scroll container's content exceeds its visible height (e.g. inner `ul` with overflow-y: auto).
   *
   * @param {HTMLElement} scrollRoot
   * @returns {boolean}
   */
  #unifiedListContentOverflowsScrollRoot(scrollRoot) {
    return (
      Math.ceil(scrollRoot.scrollHeight) > Math.floor(scrollRoot.clientHeight) + UNIFIED_SHOW_MORE_OVERFLOW_SLACK_PX
    );
  }

  /**
   * Max height (border box) a list can use inside its flex/grid wrapper — caps viewport math.
   *
   * @param {HTMLElement} listEl
   * @param {string} wrapperSelector
   * @returns {number}
   */
  #unifiedListLayoutOuterMax(listEl, wrapperSelector) {
    const wrapper = listEl.closest(wrapperSelector);
    if (!(wrapper instanceof HTMLElement)) return Number.POSITIVE_INFINITY;

    let usedBeforeList = 0;
    for (const child of wrapper.children) {
      if (child === listEl) break;
      if (child instanceof HTMLElement) usedBeforeList += child.offsetHeight;
    }
    return Math.max(0, wrapper.clientHeight - usedBeforeList);
  }

  /**
   * Collapse list, hide show-more control, reset more/less labels (unified drawer).
   *
   * @param {HTMLElement} listEl
   * @param {string} expandedClass
   * @param {HTMLButtonElement} button
   * @param {string} moreLabelSelector
   * @param {string} lessLabelSelector
   */
  #unifiedShowMoreResetToCollapsed(listEl, expandedClass, button, moreLabelSelector, lessLabelSelector) {
    listEl.classList.remove(expandedClass);
    listEl.style.maxHeight = '';
    button.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    const moreLabel = button.querySelector(moreLabelSelector);
    const lessLabel = button.querySelector(lessLabelSelector);
    if (moreLabel instanceof HTMLElement && lessLabel instanceof HTMLElement) {
      moreLabel.hidden = false;
      lessLabel.hidden = true;
    }
  }

  /**
   * @param {object} config
   * @param {HTMLElement} config.listEl
   * @param {HTMLElement} config.sectionEl
   * @param {string} config.buttonSelector
   * @param {string} config.rowItemSelector
   * @param {string} config.expandedClass
   * @param {string} config.reserveCssVar
   * @param {number} config.reserveDefault
   * @param {string} config.capCssVar
   * @param {string} config.wrapperSelector
   * @param {string} config.moreLabelSelector
   * @param {string} config.lessLabelSelector
   * @param {(px: number) => void} config.persistCollapsedHeightPx
   * @param {string} [config.overflowScrollSelector] - Optional selector (relative to listEl) for the element that scrolls; used for overflow detection when content scrolls inside a child (e.g. `scroll-hint` around the list).
   */
  #syncUnifiedShowMoreList(config) {
    const button = config.sectionEl.querySelector(config.buttonSelector);
    if (!(button instanceof HTMLButtonElement)) return;

    /** @type {HTMLElement[]} */
    const items = [];
    for (const el of config.listEl.querySelectorAll(config.rowItemSelector)) {
      if (el instanceof HTMLElement && this.#unifiedListLiVisible(config.listEl, el)) {
        items.push(el);
      }
    }
    const first = items[0];
    const rowStride = first ? this.#unifiedListRowStride(items) : 0;

    const collapseUi = () =>
      this.#unifiedShowMoreResetToCollapsed(
        config.listEl,
        config.expandedClass,
        button,
        config.moreLabelSelector,
        config.lessLabelSelector,
      );

    if (!first || rowStride <= 0) {
      collapseUi();
      return;
    }

    const sectionStyles = getComputedStyle(config.sectionEl);
    const reserve = parseFloat(sectionStyles.getPropertyValue(config.reserveCssVar).trim()) || config.reserveDefault;
    let capPx = parseFloat(sectionStyles.getPropertyValue(config.capCssVar).trim());
    if (!Number.isFinite(capPx) || capPx <= 0) {
      capPx = Infinity;
    }

    const vv = window.visualViewport;
    const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const listTop = config.listEl.getBoundingClientRect().top;
    let availableBelow = viewportBottom - listTop - reserve;
    if (!Number.isFinite(availableBelow)) availableBelow = 0;
    availableBelow = Math.max(0, availableBelow);

    const listCs = getComputedStyle(config.listEl);
    const listPadY = (parseFloat(listCs.paddingTop) || 0) + (parseFloat(listCs.paddingBottom) || 0);
    const rawLayoutMax = this.#unifiedListLayoutOuterMax(config.listEl, config.wrapperSelector);
    const layoutOuterMax = rawLayoutMax > 0 ? rawLayoutMax : Number.POSITIVE_INFINITY;
    const outerBudget = Math.max(0, Math.min(availableBelow, capPx, layoutOuterMax));
    const innerBudget = Math.max(0, outerBudget - listPadY);
    let rows = Math.floor(innerBudget / rowStride);
    if (rows < 1) rows = 1;
    const collapsedHeight = this.#unifiedListClampedCollapsedHeightPx(config.listEl, items, rows, rowStride, listPadY);

    config.persistCollapsedHeightPx(collapsedHeight);

    const isExpanded = config.listEl.classList.contains(config.expandedClass);
    const overflowScrollRoot =
      config.overflowScrollSelector != null ? config.listEl.querySelector(config.overflowScrollSelector) : null;
    const needsShowMore =
      overflowScrollRoot instanceof HTMLElement
        ? this.#unifiedListContentOverflowsScrollRoot(overflowScrollRoot)
        : this.#unifiedListContentOverflowsParentSlot(config.listEl);

    if (!needsShowMore) {
      collapseUi();
      return;
    }

    button.hidden = false;
    if (!isExpanded) {
      config.listEl.style.maxHeight = `${collapsedHeight}px`;
    }
  }

  #teardownUnifiedLocalizationShowMore() {
    if (this.#unifiedLocalizationResizeObserver) {
      this.#unifiedLocalizationResizeObserver.disconnect();
      this.#unifiedLocalizationResizeObserver = null;
    }
    window.removeEventListener('resize', this.#onUnifiedViewportChange);
    const vv = window.visualViewport;
    vv?.removeEventListener('resize', this.#onUnifiedViewportChange);
    vv?.removeEventListener('scroll', this.#onUnifiedViewportChange);
  }

  #onUnifiedViewportChange = () => {
    this.#updateUnifiedCountryShowMore();
    this.#updateUnifiedLanguageShowMore();
  };

  #setupUnifiedLocalizationShowMore() {
    if (!this.#unifiedForm) return;
    if (!this.refs.countryList && !this.refs.languageList) return;

    this.#teardownUnifiedLocalizationShowMore();

    const dialog = this.closest('dialog');

    this.#unifiedLocalizationResizeObserver = new ResizeObserver(() => {
      this.#updateUnifiedCountryShowMore();
      this.#updateUnifiedLanguageShowMore();
    });
    if (this.refs.countryList) {
      this.#unifiedLocalizationResizeObserver.observe(this.refs.countryList);
      const countryScrollRoot = this.#getCountryListScrollRoot();
      if (countryScrollRoot) {
        this.#unifiedLocalizationResizeObserver.observe(countryScrollRoot);
      }
    }
    if (this.refs.languageList) {
      this.#unifiedLocalizationResizeObserver.observe(this.refs.languageList);
      const languageScrollRoot = this.#getLanguageListScrollRoot();
      if (languageScrollRoot) {
        this.#unifiedLocalizationResizeObserver.observe(languageScrollRoot);
      }
    }
    if (dialog instanceof HTMLDialogElement) {
      this.#unifiedLocalizationResizeObserver.observe(dialog);
    }

    window.addEventListener('resize', this.#onUnifiedViewportChange);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', this.#onUnifiedViewportChange);
    vv?.addEventListener('scroll', this.#onUnifiedViewportChange);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#updateUnifiedCountryShowMore();
        this.#updateUnifiedLanguageShowMore();
      });
    });
  }

  #updateUnifiedCountryShowMore() {
    const unified = this.#unifiedForm;
    const countryList = this.refs.countryList;
    if (!unified || !countryList) return;

    const section = unified.querySelector('.unified-localization-form__country');
    if (!(section instanceof HTMLElement)) return;

    this.#syncUnifiedShowMoreList({
      listEl: countryList,
      sectionEl: section,
      buttonSelector: 'button.show-more-countries',
      rowItemSelector: 'li[data-value]',
      expandedClass: 'country-selector__list--unified-expanded',
      reserveCssVar: '--unified-country-show-more-reserve',
      reserveDefault: 140,
      capCssVar: '--unified-country-collapsed-max-px',
      wrapperSelector: '.country-selector-form__wrapper',
      moreLabelSelector: '.show-more-countries__label--more',
      lessLabelSelector: '.show-more-countries__label--less',
      overflowScrollSelector: 'scroll-hint.localization-form__countries-scroll',
      persistCollapsedHeightPx: (px) => {
        this.#unifiedCollapsedHeightPx = px;
      },
    });
  }

  #updateUnifiedLanguageShowMore() {
    const unified = this.#unifiedForm;
    const languageList = this.refs.languageList;
    if (!unified || !languageList) return;

    const section = unified.querySelector('.unified-localization-form__language');
    if (!(section instanceof HTMLElement)) return;

    this.#syncUnifiedShowMoreList({
      listEl: languageList,
      sectionEl: section,
      buttonSelector: 'button.show-more-languages',
      rowItemSelector: 'li.unified-localization-form__language-item',
      expandedClass: 'language-selector__list--unified-expanded',
      reserveCssVar: '--unified-language-show-more-reserve',
      reserveDefault: 100,
      capCssVar: '--unified-language-collapsed-max-px',
      wrapperSelector: '.language-selector-form__wrapper',
      moreLabelSelector: '.show-more-languages__label--more',
      lessLabelSelector: '.show-more-languages__label--less',
      overflowScrollSelector: 'scroll-hint.localization-form__languages-scroll',
      persistCollapsedHeightPx: (px) => {
        this.#unifiedLanguageCollapsedHeightPx = px;
      },
    });
  }

  /**
   * Updates aria-current on country rows when selection changes without submitting (defer-submit mode).
   *
   * @param {string} isoCode - Selected country ISO code.
   */
  #syncCountryCurrentIndicator(isoCode) {
    const { countryListItems } = this.refs;
    if (!countryListItems?.length) return;

    for (const item of countryListItems) {
      if (item.dataset.value === isoCode) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Updates aria-current on unified language rows when selection changes without submitting.
   *
   * @param {string} isoCode - Selected language ISO code.
   */
  #syncLanguageCurrentIndicator(isoCode) {
    const { languageListItems } = this.refs;
    if (!languageListItems?.length) return;

    for (const item of languageListItems) {
      if (item.dataset.value === isoCode) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Changes the language of the localization form.
   *
   * @param {Event} event - The event object.
   */
  changeLanguage(event) {
    const { form, languageInput } = this.refs;
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value && languageInput) {
      languageInput.value = value;
      this.resizeLanguageInput();
      if (this.#deferSubmit) return;
      form?.submit();
    }
  }

  resizeLanguageInput() {
    const { languageInput } = this.refs;

    if (!languageInput || !(languageInput instanceof HTMLSelectElement)) return;

    // Hide all options except the selected option
    for (const option of languageInput.options) {
      if (!option.selected) {
        option.dataset.optionLabel = option.textContent || '';
        option.innerText = '';
      }
    }

    // Calculate the width of the select element (which is based on the width of the widest option)
    languageInput.style.width = 'fit-content';
    const originalElementWidth = `${Math.ceil(languageInput.offsetWidth) + 1}px`;

    // Fix the width of the select element
    if (languageInput.offsetWidth > 0) {
      languageInput.style.width = originalElementWidth;
    }

    // Add back all option labels
    for (const option of languageInput.options) {
      if (option.dataset.optionLabel) {
        option.textContent = option.dataset.optionLabel;
        delete option.dataset.optionLabel;
      }
    }
  }

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(
    searchValue,
    countryEl,
    options = {
      // Which data types (label, alias, iso) to match against
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      // If true, the search value must match the start of the label
      labelMatchStart: false,
      // If true, a result will not display unless the search value equals an alias in its entirety
      aliasExactMatch: false,
    },
  ) {
    let matchTypes = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const countryName = normalizeString(countryEl.querySelector('.country')?.textContent ?? '');

      if (!countryName) return matchTypes;

      matchTypes.label = options.labelMatchStart
        ? countryName.startsWith(searchValue)
        : countryName.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(countryEl.querySelector('.localization-form__currency')?.textContent ?? '');
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? '') == searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(',').map((alias) => normalizeString(alias));

      if (!countryAliases) return matchTypes;

      matchTypes.alias =
        countryAliases.length > 0 &&
        countryAliases.find((alias) =>
          options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue),
        ) !== undefined;
    }

    return matchTypes;
  }

  /**
   * Smallest index i in `text` such that the normalized prefix text.slice(0, i) has length >= normOffset.
   * Needed because normalizeString strips diacritics, so normalized indices do not match raw slice positions.
   *
   * @param {string} text
   * @param {number} normOffset
   * @returns {number}
   */
  #mapNormalizedOffsetToOriginalIndex(text, normOffset) {
    if (normOffset <= 0) return 0;
    for (let i = 0; i <= text.length; i++) {
      if (normalizeString(text.slice(0, i)).length >= normOffset) {
        return i;
      }
    }
    return text.length;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) return text ?? '';

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) return text;

    const endIndex = startIndex + normalizedSearch.length;
    const startOrig = this.#mapNormalizedOffsetToOriginalIndex(text, startIndex);
    const endOrig = this.#mapNormalizedOffsetToOriginalIndex(text, endIndex);
    const before = text.slice(0, startOrig);
    const match = text.slice(startOrig, endOrig);
    const after = text.slice(endOrig);

    let result = '';
    if (before) {
      result += before;
    }
    result += `<mark>${match}</mark>`;
    if (after) {
      result += after;
    }
    return result;
  }

  /**
   * Filters the countries based on the search value.
   */
  filterCountries() {
    const { countryList, countryListItems, liveRegion, noResultsMessage, popularCountries, resetButton, search } =
      this.refs;
    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    resetButton.toggleAttribute('hidden', !searchValue);

    if (popularCountries) {
      popularCountries.toggleAttribute('hidden', Boolean(searchValue));
    }

    const wrapper = this.querySelector('.country-selector-form__wrapper');
    if (wrapper) {
      wrapper.classList.toggle('is-searching', !!searchValue);
    }

    for (const countryEl of countryListItems) {
      if (searchValue === '') {
        countryEl.removeAttribute('hidden');
        const countrySpan = countryEl.querySelector('.country');
        if (countrySpan) {
          // eslint-disable-next-line no-self-assign
          countrySpan.textContent = countrySpan.textContent;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, countryEl);

        // In the future, we could reorder/rank filtered results based on the match types
        if (matches.label || matches.alias || matches.iso || matches.currency) {
          countryEl.removeAttribute('hidden');
          const countrySpan = countryEl.querySelector('.country');
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          countryEl.setAttribute('hidden', '');
        }
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace('[count]', `${countVisibleCountries}`);
    }

    noResultsMessage.hidden = countVisibleCountries > 0;
    const countryScrollRoot = this.#getCountryListScrollRoot();
    if (countryScrollRoot) {
      countryScrollRoot.scrollTop = 0;
    } else {
      countryList.scrollTop = 0;
    }
    this.#updateUnifiedCountryShowMore();
    this.#updateUnifiedLanguageShowMore();
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const { countryListItems } = this.refs;
    const focusableItems = countryListItems.filter((item) => !item.hasAttribute('hidden'));
    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
    const focusedItem = focusableItems[focusedItemIndex];
    let itemToFocus;

    if (direction === 'UP') {
      itemToFocus =
        focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
    }

    if (focusedItem) {
      focusedItem.setAttribute('aria-selected', 'false');
    }
    itemToFocus?.setAttribute('aria-selected', 'true');
    itemToFocus?.focus();
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  resetCountriesFilter(event) {
    const { search } = this.refs;

    event.stopPropagation();
    search.value = '';
    this.filterCountries();
    search.setAttribute('aria-activedescendant', '');
    search.focus();
  }

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Resets the form.
   */
  resetForm() {
    const { search } = this.refs;

    if (!search) return;

    if (search.value != '') {
      search.value = '';
      this.filterCountries();
      search.setAttribute('aria-activedescendant', '');
    }
  }

  /**
   * Focuses the search input.
   */
  focusSearchInput = () => {
    const { search } = this.refs;

    search?.focus();
  };

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
  };
}

/**
 * A custom element that displays a dropdown localization form.
 *
 * @typedef {object} DropdownRefs
 * @property {HTMLButtonElement} button - The button element.
 * @property {HTMLDivElement} panel - The panel element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DropdownRefs>}
 */
class DropdownLocalizationComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Toggles the panel.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Shows the panel.
   */
  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    onAnimationEnd(this.refs.panel, () => {
      this.#updateWidth();
      this.refs.localizationForm?.focusSearchInput();
    });
  }

  /**
   * Hides the panel.
   */
  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
    this.refs.localizationForm?.resetForm();
  };

  /**
   * Handles the click outside event.
   *
   * @param {PointerEvent} event - The event object.
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Updates the width of the panel.
   */
  #updateWidth() {
    this.style.setProperty('--width', `${this.refs.localizationForm.offsetWidth}px`);
  }

  /**
   * Handles the keyup event.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #handleKeyUp = (event) => {
    switch (event.key) {
      case 'Escape':
        this.hidePanel();
        event.stopPropagation();
        this.refs.button?.focus();
        break;
    }
  };
}

/**
 * A custom element that displays a drawer localization form.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DrawerRefs>}
 */
class DrawerLocalizationComponent extends Component {
  /** @type {HTMLElement | null} */
  #countryListInnerScrollRoot = null;

  /**
   * Toggles the dialog.
   *
   * @param {ToggleEvent} event - The event object.
   */
  toggle(event) {
    const { target } = event;
    const { localizationForm } = this.refs;

    if (!localizationForm || !(target instanceof HTMLDetailsElement)) return;

    const countryWrapper = localizationForm.querySelector('.country-selector-form__wrapper');
    const innerScroll =
      localizationForm.querySelector('scroll-hint.localization-form__countries-scroll') ??
      localizationForm.querySelector('ul.countries');

    if (target.open) {
      if (countryWrapper) countryWrapper.addEventListener('scroll', this.#onCountryListScroll);
      if (innerScroll instanceof HTMLElement && innerScroll !== countryWrapper) {
        innerScroll.addEventListener('scroll', this.#onCountryListScroll);
        this.#countryListInnerScrollRoot = innerScroll;
      }
      onAnimationEnd(target, localizationForm.focusSearchInput);
    } else {
      countryWrapper?.removeEventListener('scroll', this.#onCountryListScroll);
      this.#countryListInnerScrollRoot?.removeEventListener('scroll', this.#onCountryListScroll);
      this.#countryListInnerScrollRoot = null;
      localizationForm.resetForm();
    }
  }

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
  };
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}

if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}

if (!customElements.get('drawer-localization-component')) {
  customElements.define('drawer-localization-component', DrawerLocalizationComponent);
}
